import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const DetailSection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-xs font-bold uppercase tracking-widest mb-3 pb-2 border-b" style={{ color: '#D4A520', borderColor: '#E5E7EB' }}>{title}</h3>
    {children}
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-1.5">
    <span className="text-xs" style={{ color: '#9CA3AF' }}>{label}</span>
    <span className="text-xs font-medium text-right max-w-[55%]" style={{ color: '#0B1F3B' }}>{value || '—'}</span>
  </div>
);

const STATUS_STYLES = {
  pending: { bg: 'rgba(212,165,32,0.15)', text: '#B8860B', label: 'Pending' },
  approved: { bg: 'rgba(5,150,105,0.15)', text: '#059669', label: 'Approved' },
  rejected: { bg: 'rgba(220,38,38,0.15)', text: '#DC2626', label: 'Rejected' },
  under_review: { bg: 'rgba(11,31,59,0.1)', text: '#0B1F3B', label: 'Under Review' },
};

function GoldSpinner() {
  return (
    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#D4A520" strokeWidth="3" strokeDasharray="30 70"/>
    </svg>
  );
}

export default function ApplicationDetailPanel({ application: app, onClose, onApprove, onReject, onUnderReview }) {
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [pitchDeckUrl, setPitchDeckUrl] = useState(null);
  const [loadingDeck, setLoadingDeck] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'approve' | 'underReview' | 'reject'

  const status = STATUS_STYLES?.[app?.status] || STATUS_STYLES?.pending;
  const isAnyLoading = actionLoading !== null;

  const handleGetPitchDeck = async () => {
    if (!app?.pitch_deck_url) return;
    setLoadingDeck(true);
    try {
      const { data, error } = await supabase?.storage?.from('showcase-pitchdecks')?.createSignedUrl(app?.pitch_deck_url, 3600);
      if (!error && data?.signedUrl) {
        setPitchDeckUrl(data?.signedUrl);
        window.open(data?.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to get pitch deck URL', err);
    } finally {
      setLoadingDeck(false);
    }
  };

  const isApproved = app?.status === 'approved';
  const isRejected = app?.status === 'rejected';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col bg-white shadow-2xl overflow-hidden"
        style={{ width: '100%', maxWidth: 520 }}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ background: '#0B1F3B', borderColor: '#1a3a6b' }}>
          <div>
            <h2 className="font-bold text-white text-lg">{app?.business_name}</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Application Review</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Status badge with smooth color transition */}
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"
              style={{
                background: status?.bg,
                color: status?.text,
                transition: 'background 0.4s ease, color 0.4s ease',
              }}
            >
              {status?.label}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Founder */}
          <DetailSection title="Founder Information">
            <div className="flex items-center gap-4 mb-4">
              {app?.profile_photo_url ? (
                <img src={app?.profile_photo_url} alt={app?.founder_name} className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: '#D4A520' }} />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl" style={{ background: '#0B1F3B', color: '#D4A520' }}>
                  {app?.founder_name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-bold" style={{ color: '#0B1F3B' }}>{app?.founder_name}</p>
                <p className="text-sm" style={{ color: '#6B7280' }}>{app?.email}</p>
              </div>
            </div>
            <DetailRow label="Phone" value={app?.phone} />
            <DetailRow label="Location" value={app?.location} />
            {app?.linkedin_url && (
              <div className="flex justify-between items-center py-1.5">
                <span className="text-xs" style={{ color: '#9CA3AF' }}>LinkedIn</span>
                <a href={app?.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium" style={{ color: '#D4A520' }}>View Profile →</a>
              </div>
            )}
          </DetailSection>

          {/* Business */}
          <DetailSection title="Business Details">
            <DetailRow label="Business Name" value={app?.business_name} />
            <DetailRow label="Sector" value={app?.sector} />
            <DetailRow label="Funding Stage" value={app?.funding_stage} />
            <DetailRow label="Year Founded" value={app?.year_founded} />
            <DetailRow label="Employees" value={app?.employees} />
            {app?.website_url && (
              <div className="flex justify-between items-center py-1.5">
                <span className="text-xs" style={{ color: '#9CA3AF' }}>Website</span>
                <a href={app?.website_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium" style={{ color: '#D4A520' }}>Visit Site →</a>
              </div>
            )}
            {app?.description && (
              <div className="mt-2 p-3 rounded-lg" style={{ background: '#F9FAFB' }}>
                <p className="text-xs" style={{ color: '#374151' }}>{app?.description}</p>
              </div>
            )}
          </DetailSection>

          {/* Investment */}
          <DetailSection title="Investment Ask">
            <DetailRow label="Min. Investment" value={app?.min_investment ? `$${Number(app?.min_investment)?.toLocaleString()}` : null} />
            <DetailRow label="Funding Goal" value={app?.funding_goal ? `$${Number(app?.funding_goal)?.toLocaleString()}` : null} />
            <DetailRow label="Projected ROI" value={app?.projected_roi ? `${app?.projected_roi}%` : null} />
            <DetailRow label="Timeline" value={app?.investment_timeline} />
            <DetailRow label="Women-Led" value={app?.is_women_led ? 'Yes ✓' : 'No'} />
            {app?.use_of_funds && (
              <div className="mt-2 p-3 rounded-lg" style={{ background: '#F9FAFB' }}>
                <p className="text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Use of Funds</p>
                <p className="text-xs" style={{ color: '#374151' }}>{app?.use_of_funds}</p>
              </div>
            )}
            {app?.pitch_deck_url && (
              <button
                onClick={handleGetPitchDeck}
                disabled={loadingDeck}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors hover:bg-[#D4A520]/5 disabled:opacity-60"
                style={{ borderColor: '#D4A520', color: '#D4A520' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 10v6M9 13l3 3 3-3M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {loadingDeck ? 'Loading...' : 'Download Pitch Deck'}
              </button>
            )}
          </DetailSection>

          {app?.admin_notes && (
            <DetailSection title="Admin Notes">
              <div className="p-3 rounded-lg" style={{ background: '#FEF3C7' }}>
                <p className="text-xs" style={{ color: '#92400E' }}>{app?.admin_notes}</p>
              </div>
            </DetailSection>
          )}

          {/* Reject Form */}
          {showRejectForm && (
            <div className="mb-4 p-4 rounded-xl border-2" style={{ borderColor: '#DC2626', background: '#FEF2F2' }}>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#DC2626' }}>Rejection Notes (optional)</label>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e?.target?.value)}
                placeholder="Provide feedback to the applicant..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-red-200 text-sm outline-none resize-none"
                style={{ color: '#374151' }}
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold border border-gray-200"
                  style={{ color: '#6B7280' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    onReject(app, rejectNotes, setActionLoading);
                  }}
                  disabled={isAnyLoading}
                  className="flex-1 py-2 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2"
                  style={{
                    background: '#DC2626',
                    opacity: isAnyLoading ? 0.5 : 1,
                    cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {actionLoading === 'reject' ? (
                    <><GoldSpinner /><span>Rejecting...</span></>
                  ) : 'Confirm Reject'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          {!isApproved && !isRejected ? (
            <div className="flex flex-col gap-2">
              {/* Approve Button */}
              <div className="relative group">
                <button
                  onClick={() => !isAnyLoading && onApprove(app, setActionLoading)}
                  disabled={isAnyLoading}
                  className="w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2"
                  style={{
                    background: '#D4A520',
                    color: '#0B1F3B',
                    opacity: isAnyLoading ? 0.5 : 1,
                    cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.2s',
                    pointerEvents: isAnyLoading ? 'none' : 'auto',
                  }}
                >
                  {actionLoading === 'approve' ? (
                    <><GoldSpinner /><span>Approving...</span></>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#0B1F3B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg><span>Approve &amp; Publish</span></>
                  )}
                </button>
              </div>

              <div className="flex gap-2">
                {/* Request More Info / Under Review */}
                {app?.status !== 'under_review' && (
                  <div className="flex-1 relative group">
                    <button
                      onClick={() => !isAnyLoading && onUnderReview(app, setActionLoading)}
                      disabled={isAnyLoading}
                      className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2"
                      style={{
                        borderColor: '#0B1F3B',
                        color: '#0B1F3B',
                        opacity: isAnyLoading ? 0.5 : 1,
                        cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                        transition: 'opacity 0.2s',
                        pointerEvents: isAnyLoading ? 'none' : 'auto',
                        background: 'white',
                      }}
                    >
                      {actionLoading === 'underReview' ? (
                        <><GoldSpinner /><span>Updating...</span></>
                      ) : (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M8 12h8M8 8h5M8 16h3" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round"/><path d="M4 4h16v16H4z" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg><span>Request More Info</span></>
                      )}
                    </button>
                  </div>
                )}

                {/* Reject Button */}
                <div className="flex-1 relative group">
                  <button
                    onClick={() => !isAnyLoading && setShowRejectForm(true)}
                    disabled={isAnyLoading}
                    className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2"
                    style={{
                      borderColor: '#DC2626',
                      color: '#DC2626',
                      opacity: isAnyLoading ? 0.5 : 1,
                      cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.2s',
                      pointerEvents: isAnyLoading ? 'none' : 'auto',
                      background: 'white',
                    }}
                  >
                    {actionLoading === 'reject' ? (
                      <><GoldSpinner /><span>Rejecting...</span></>
                    ) : (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"/></svg><span>Reject</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Already approved — show approve button disabled with tooltip */}
              {isApproved && (
                <div className="flex flex-col gap-2">
                  <div className="relative group">
                    <button
                      disabled
                      title="Already approved"
                      className="w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 cursor-not-allowed"
                      style={{ background: 'rgba(212,165,32,0.2)', color: '#B8860B', opacity: 0.6 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#B8860B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <span>Approve &amp; Publish</span>
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Already approved
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 relative group">
                      <button
                        onClick={() => !isAnyLoading && onUnderReview(app, setActionLoading)}
                        disabled={isAnyLoading}
                        className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2"
                        style={{
                          borderColor: '#0B1F3B', color: '#0B1F3B',
                          opacity: isAnyLoading ? 0.5 : 1,
                          cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                          background: 'white',
                        }}
                      >
                        {actionLoading === 'underReview' ? <><GoldSpinner /><span>Updating...</span></> : 'Request More Info'}
                      </button>
                    </div>
                    <div className="flex-1 relative group">
                      <button
                        disabled
                        title="Already rejected"
                        className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2 cursor-not-allowed"
                        style={{ borderColor: '#9CA3AF', color: '#9CA3AF', opacity: 0.5, background: 'white' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round"/></svg>
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Already rejected — show reject button disabled with tooltip */}
              {isRejected && (
                <div className="flex flex-col gap-2">
                  <div className="relative group">
                    <button
                      onClick={() => !isAnyLoading && onApprove(app, setActionLoading)}
                      disabled={isAnyLoading}
                      className="w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2"
                      style={{
                        background: '#D4A520', color: '#0B1F3B',
                        opacity: isAnyLoading ? 0.5 : 1,
                        cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {actionLoading === 'approve' ? <><GoldSpinner /><span>Approving...</span></> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#0B1F3B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg><span>Approve &amp; Publish</span></>}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <button
                        onClick={() => !isAnyLoading && onUnderReview(app, setActionLoading)}
                        disabled={isAnyLoading}
                        className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2"
                        style={{
                          borderColor: '#0B1F3B', color: '#0B1F3B',
                          opacity: isAnyLoading ? 0.5 : 1,
                          cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                          background: 'white',
                        }}
                      >
                        {actionLoading === 'underReview' ? <><GoldSpinner /><span>Updating...</span></> : 'Request More Info'}
                      </button>
                    </div>
                    <div className="flex-1 relative group">
                      <button
                        disabled
                        title="Already rejected"
                        className="w-full py-3 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-2 cursor-not-allowed"
                        style={{ borderColor: '#9CA3AF', color: '#9CA3AF', opacity: 0.5, background: 'white' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round"/></svg>
                        <span>Reject</span>
                      </button>
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Already rejected
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
